import React from 'react';
import { Payment, ArrowForward, CheckCircle, AddCircleOutline, OpenInNew, Reply, ReceiptLong } from '@mui/icons-material';
import ParameterTable from "../components/documentation/ParameterTable";
import CodeBlock from "../components/documentation/CodeBlock";

const PaymentFlow = () => {
    const flowSteps = [
        { step: 1, title: 'Initiation', desc: 'Création demande', Icon: AddCircleOutline, color: 'text-blue-700', bg: 'bg-blue-100' },
        { step: 2, title: 'Redirection', desc: 'Vers Netikash', Icon: OpenInNew, color: 'text-amber-700', bg: 'bg-amber-100' },
        { step: 3, title: 'Paiement', desc: 'Client paye', Icon: Payment, color: 'text-purple-700', bg: 'bg-purple-100' },
        { step: 4, title: 'Callback', desc: 'Retour marchand', Icon: Reply, color: 'text-sky-700', bg: 'bg-sky-100' },
        { step: 5, title: 'Confirmation', desc: 'Transaction OK', Icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-100' }
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="flex justify-center mb-6">
                        <div className="p-4 bg-green-100 rounded-2xl">
                            <Payment className="text-4xl text-green-600" />
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Flux de Paiement</h1>
                    <p className="text-xl text-gray-600">
                        Comprenez le cycle complet d'une transaction Netikash
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                    {/* Flow Overview */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Vue d'Ensemble du Flux</h2>

                        <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-emerald-50 rounded-2xl p-6 mb-6 border border-amber-100">
                            <div className="grid md:grid-cols-5 gap-4">
                                {flowSteps.map((item) => (
                                    <div key={item.step} className="relative">
                                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm h-full">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className={`w-11 h-11 ${item.bg} rounded-xl flex items-center justify-center`}>
                                                    <item.Icon className={`${item.color}`} />
                                                </div>
                                                <div className="text-xs font-semibold text-gray-500">Étape {item.step}</div>
                                            </div>
                                            <div className="font-bold text-sm text-gray-900">{item.title}</div>
                                            <div className="text-xs text-gray-600 mt-1">{item.desc}</div>
                                        </div>
                                        {item.step < 5 && (
                                            <ArrowForward className="absolute -right-4 top-1/2 transform -translate-y-1/2 text-amber-500 hidden md:block" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Step 1: Initiation */}
                    <section className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                            <span className="w-9 h-9 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-3 font-black">1</span>
                            Initiation du Paiement
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Ce flux décrit le <strong>lien de paiement</strong> (endpoint POST <code className="bg-gray-100 px-1 rounded">/transactions/requests/cli-payments</code>). Pour un paiement vers un compte spécifique (receiver_id, method), consultez la section Demande de Paiement dans la documentation.
                        </p>

                        <ParameterTable
                            title="Paramètres de la demande"
                            parameters={[
                                { name: 'amount', type: 'double', required: 'oui', description: 'Le montant à payer' },
                                { name: 'ref', type: 'string', required: 'oui', description: 'La référence du paiement (numéro de facture, identifiant de commande, ...)' },
                                { name: 'currency', type: 'string', required: 'oui', description: 'Devise de la transaction (USD ou CDF en majuscules)' },
                                { name: 'referer_url', type: 'string', required: 'oui', description: 'URL de redirection après paiement' }
                            ]}
                        />

                        <CodeBlock
                            code={JSON.stringify({
                                amount: 100.50,
                                currency: "USD",
                                ref: "CMD-2024-001",
                                referer_url: "https://votre-site.com/confirmation",
                            }, null, 2)}
                            language="json"
                        />
                    </section>

                    {/* Step 2: Redirection */}
                    <section className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                            <span className="w-9 h-9 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center mr-3 font-black">2</span>
                            Redirection vers Netikash
                        </h3>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-4">
                            <p className="text-amber-900">
                                Après création de la demande, redirigez votre client vers l'URL fournie dans <code className="bg-yellow-100 px-1 rounded">link</code>
                            </p>
                        </div>

                        <CodeBlock
                            code={`// Réponse de création de paiement (flux cli-payments)
{
  "link": "https://pay.netikash.com/payment-requests/<request_id>",
  "expires_at": "2024-01-15T23:59:59Z",
  "status": "requested",
  "trans": "PMT-xxxx",
  "message": "Paiement initialisee",
  "error": false
}`}
                            language="json"
                        />
                    </section>

                    {/* Step 3: Paiement Client */}
                    <section className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                            <span className="w-9 h-9 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center mr-3 font-black">3</span>
                            Interface de Paiement
                        </h3>

                        <div className="grid lg:grid-cols-2 gap-6">
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <h4 className="font-semibold mb-2">Étapes Client</h4>
                                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                                    <li>Saisie des informations de paiement</li>
                                    <li>Validation de la transaction</li>
                                    <li>Authentification (optionnel)</li>
                                    <li>Confirmation du paiement</li>
                                </ol>
                            </div>

                            <div className="bg-gray-100 p-4 rounded-lg">
                                <h4 className="font-semibold mb-2">Sécurité</h4>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-center">
                                        <CheckCircle className="text-green-500 mr-2 text-sm" />
                                        Chiffrement TLS 1.3
                                    </li>

                                    <li className="flex items-center">
                                        <CheckCircle className="text-green-500 mr-2 text-sm" />
                                        Tokenisation des cartes
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Step 4: Callback */}
                    <section className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                            <span className="w-9 h-9 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center mr-3 font-black">4</span>
                            Webhook & Callback
                        </h3>

                        <ParameterTable
                            title="Paramètres du Webhook"
                            parameters={[
                                {name: 'event', type: 'string', required: 'oui', description: "Type d'evenement"},
                                {name: 'ref', type: 'string', required: 'oui', description: "Reference de la demande de paiement (ID de votre facture, etc)"},
                                {name: 'trx', type: 'string', required: 'oui', description: 'ID de la demande de paiement'},
                                {name: 'status', type: 'string', required: 'oui', description: 'État de la demande de paiement'},
                            ]}
                        />

                        <CodeBlock
                            code={`// Exemple de payload webhook
{
  "event": "payment.completed",
    "trx": "txn_abc123",
    "ref": "CMD-2024-001",
    "status": "completed",
  }`}
                            language="json"
                        />
                    </section>

                    {/* Step 5: Confirmation */}
                    <section className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                            <span className="w-9 h-9 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mr-3 font-black">5</span>
                            Confirmation serveur
                        </h3>

                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-4">
                            <p className="text-emerald-900">
                                Considérez un paiement comme réussi uniquement après validation côté serveur (webhook et/ou vérification). Pour vérifier le statut, appelez <code className="bg-white px-1 rounded">GET /transactions/requests/{'{id}'}</code> en utilisant l&apos;identifiant de la demande (présent dans l&apos;URL du champ <code className="bg-white px-1 rounded">link</code> ou retourné à la création dans <code className="bg-white px-1 rounded">trans_id</code>).
                            </p>
                        </div>

                        <CodeBlock
                            code={`// Exemple (pseudo-code)\n// 1) Recevoir le webhook\n// 2) Vérifier côté serveur le statut final (GET /transactions/requests/{id}) avant de livrer\n\nif (payload.status === 'completed') {\n  // livrer la commande / créditer le compte\n} else {\n  // gérer requested/approved/failed/expired...\n}`}
                            language="javascript"
                        />
                    </section>

                    {/* Résumé rapide */}
                    <section className="mb-10">
                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-11 h-11 rounded-xl bg-gray-200 flex items-center justify-center flex-shrink-0">
                                    <ReceiptLong className="text-gray-700" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-900">Résumé du flux</h4>
                                    <ol className="mt-2 space-y-2 text-sm text-gray-700">
                                        <li className="flex items-start gap-2"><span className="font-bold">1.</span><span>Créer une demande de paiement (API).</span></li>
                                        <li className="flex items-start gap-2"><span className="font-bold">2.</span><span>Rediriger le client vers le lien de paiement.</span></li>
                                        <li className="flex items-start gap-2"><span className="font-bold">3.</span><span>Recevoir le webhook (ou callback) et traiter.</span></li>
                                        <li className="flex items-start gap-2"><span className="font-bold">4.</span><span>Vérifier côté serveur avant livraison.</span></li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </section>
{/*                    <section className="mb-8">*/}
{/*                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">*/}
{/*                            <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3">5</span>*/}
{/*                            Confirmation Finale*/}
{/*                        </h3>*/}

{/*                        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">*/}
{/*                            <p className="text-green-700">*/}
{/*                                Vérifiez toujours le statut de la transaction côté serveur avant de considérer le paiement comme réussi.*/}
{/*                            </p>*/}
{/*                        </div>*/}

{/*                        <CodeBlock*/}
{/*                            code={`// Vérification du statut*/}
{/*const response = await netikash.payments.verify(reference);*/}

{/*if (response.status === 'completed') {*/}
{/*  // Paiement confirmé*/}
{/*  await fulfillOrder(reference);*/}
{/*}`}*/}
{/*                            language="javascript"*/}
{/*                        />*/}
{/*                    </section>*/}

                    {/* Best Practices */}
                    <section className="p-6 bg-blue-50 border border-blue-200 rounded-2xl">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3">Bonnes Pratiques</h3>
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                            <ul className="space-y-2">
                                <li>• Validez toujours les montants côté serveur</li>
                                <li>• Implémentez les webhooks pour les statuts temps réel</li>
                                <li>• Gérez les cas d'expiration des paiements</li>
                            </ul>
                            <ul className="space-y-2">
                                <li>• Logguez les erreurs de paiement</li>
                                <li>• Surveillez les taux d'échec</li>
                            </ul>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PaymentFlow;